import { escapeHtml } from '../../utils/format';
import { colourGroupFor, remainingFor } from './productionUtils';

const formatDue = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return isNaN(d.getTime())
    ? String(dateStr)
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Splits work orders into print sections: one per colour (in order of first appearance,
// so the on-screen sort is kept) or a single section when not grouping by colour.
export const printSections = (workOrders, byColour) => {
  if (!byColour) return workOrders.length ? [{ key: 'all', label: null, wos: workOrders }] : [];
  const sections = new Map();
  workOrders.forEach(wo => {
    const { key, label } = colourGroupFor(wo);
    if (!sections.has(key)) sections.set(key, { key, label, wos: [] });
    sections.get(key).wos.push(wo);
  });
  return [...sections.values()];
};

// Printable A4 landscape work order sheet. Built as its own document (not window.print()
// on the app) because Safari ignores page breaks between table rows: here each colour is
// its own section that always starts on a new page, so one sheet never mixes colours.
export const buildWorkOrdersDocument = (workOrders, { title, context, byColour = true } = {}) => {
  const sections = printSections(workOrders, byColour);
  const dateText = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const sectionHtml = sections.map(section => {
    const toMake = section.wos.reduce((sum, wo) => sum + remainingFor(wo), 0);
    const rows = section.wos.map(wo => `
          <tr>
            <td class="wo">${escapeHtml(wo.serialName || '')}</td>
            <td class="part">
              <div class="desc">${escapeHtml(wo.partDescription || wo.partName || '')}</div>
              ${wo.partName && wo.partDescription ? `<div class="code">${escapeHtml(wo.partName)}</div>` : ''}
            </td>
            <td class="cust">
              ${escapeHtml(wo.customerName || wo.customerCode || '-')}
              ${wo.salesOrder ? `<div class="so">${escapeHtml(wo.salesOrder)}</div>` : ''}
            </td>
            <td class="due">${escapeHtml(formatDue(wo.endDate || wo.plannedDate))}</td>
            <td class="num">${Number(wo.quantity) || 0}</td>
            <td class="num make">${remainingFor(wo)}</td>
            <td class="tick"><span class="box"></span></td>
            <td class="tick"><span class="box"></span></td>
          </tr>`).join('');
    return `
  <section class="sheet">
    <header class="sheet-header">
      <div>
        <div class="doc-title">${escapeHtml(title || 'Work orders')}${context ? ` <span class="context">· ${escapeHtml(context)}</span>` : ''}</div>
        ${section.label ? `<h2>${escapeHtml(section.label)}</h2>` : ''}
      </div>
      <div class="sheet-meta">
        <strong>${toMake.toLocaleString()} to make</strong>
        ${section.wos.length} work order${section.wos.length !== 1 ? 's' : ''} · ${escapeHtml(dateText)}
      </div>
    </header>
    <table>
      <colgroup>
        <col class="c-wo"><col><col class="c-cust"><col class="c-due"><col class="c-num"><col class="c-num"><col class="c-tick"><col class="c-tick">
      </colgroup>
      <thead>
        <tr>
          <th>Work order</th>
          <th>Part</th>
          <th>Customer</th>
          <th>Due</th>
          <th class="num">Planned</th>
          <th class="num">To make</th>
          <th class="tick">Rejects</th>
          <th class="tick">Made</th>
        </tr>
      </thead>
      <tbody>${rows}
      </tbody>
    </table>
  </section>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title || 'Work orders')}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Helvetica Neue", Arial, sans-serif;
      color: #1a2233;
      font-size: 10pt;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* Every colour starts on a new page */
    .sheet { page-break-before: always; break-before: page; }
    .sheet:first-child { page-break-before: auto; break-before: auto; }
    .sheet-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 6mm;
      padding-bottom: 2.5mm;
      margin-bottom: 3mm;
      border-bottom: 2pt solid #243660;
    }
    .doc-title { font-size: 9.5pt; color: #5b6475; text-transform: uppercase; letter-spacing: 0.5pt; }
    .context { text-transform: none; letter-spacing: 0; }
    h2 { margin: 1mm 0 0; font-size: 18pt; color: #1a2233; }
    .sheet-meta { text-align: right; font-size: 9.5pt; color: #5b6475; line-height: 1.5; white-space: nowrap; }
    .sheet-meta strong { display: block; font-size: 14pt; color: #1a2233; }

    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    col.c-wo { width: 12%; }
    col.c-cust { width: 20%; }
    col.c-due { width: 11%; }
    col.c-num { width: 7.5%; }
    col.c-tick { width: 8%; }
    th, td { padding: 2mm 2.5mm; text-align: left; vertical-align: middle; border: 0.75pt solid #b8c0cf; }
    thead { display: table-header-group; }
    thead th {
      background: #243660;
      color: #fff;
      font-size: 8pt;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4pt;
    }
    tr { page-break-inside: avoid; break-inside: avoid; }
    tbody tr:nth-child(even) td { background: #f5f7fb; }
    .wo { font-family: Menlo, Consolas, monospace; font-weight: 700; font-size: 9pt; }
    .desc { font-size: 10pt; }
    .code, .so { font-family: Menlo, Consolas, monospace; font-size: 7.5pt; color: #6b7487; margin-top: 0.6mm; }
    .cust { font-size: 9pt; }
    .due { font-size: 9pt; white-space: nowrap; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .make { font-weight: 700; font-size: 12pt; }
    .tick { text-align: center; }
    .box { display: inline-block; width: 12mm; height: 7mm; border: 1.25pt solid #333; border-radius: 1mm; }
  </style>
</head>
<body>${sectionHtml}
</body>
</html>`;
};
